"use client";

import { useMemo, useState } from "react";
import type { PriceChartPoint } from "./price-chart";
import { formatPeriodLabel } from "@/lib/price-index/period-label";

export type HistoryPoint = {
  periodKey: string;
  avgPriceCents: number;
};

export type TimeRange = "1Y" | "5Y" | "ALL";

const RANGE_MONTHS: Record<TimeRange, number> = {
  "1Y": 12,
  "5Y": 60,
  ALL: Number.POSITIVE_INFINITY,
};

const RANGE_OPTIONS: Array<{ value: TimeRange; label: string }> = [
  { value: "1Y", label: "1Y" },
  { value: "5Y", label: "5Y" },
  { value: "ALL", label: "All" },
];

function toPoints(history: HistoryPoint[], range: TimeRange): PriceChartPoint[] {
  const sorted = history.slice().sort((a, b) =>
    a.periodKey.localeCompare(b.periodKey),
  );
  const months = RANGE_MONTHS[range];
  return (Number.isFinite(months) ? sorted.slice(-months) : sorted).map(
    (point) => ({
      label: formatPeriodLabel(point.periodKey),
      priceCents: point.avgPriceCents,
    }),
  );
}

export function usePriceSeries(history: HistoryPoint[]) {
  const [range, setRange] = useState<TimeRange>("5Y");
  const points = useMemo(() => toPoints(history, range), [history, range]);
  return { range, setRange, rangeOptions: RANGE_OPTIONS, points };
}
